import React from "react";
import { Button, Icon } from "semantic-ui-react";
import PropertyPhoto from "./PropertyPhoto";
import StrategyCard from "./StrategyCard";
import { PI_COVERAGE } from "../propertyIntelligence/piModel";

// Scoped to .bpc-card so nothing leaks into the rest of the app. Colors
// follow the Property Intelligence report (piStyles.js): navy headings,
// TransActioner blue actions, light-blue tint for the research panel.
const CARD_CSS = `
.bpc-card { background: #fff; border: 1px solid #e3e8ef; border-radius: 14px; box-shadow: 0 1px 3px rgba(22, 36, 60, 0.06); padding: 20px; }
.bpc-card.bpc-current { border-color: #b9d6f2; }
.bpc-card.bpc-bare { border: 0; box-shadow: none; padding: 0; background: transparent; }
.bpc-head { display: flex; align-items: center; gap: 20px; }
.bpc-photo { width: 160px; height: 110px; flex-shrink: 0; }
.bpc-info { flex: 1 1 auto; min-width: 0; }
.bpc-title { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.bpc-street { margin: 0; font-size: 1.3em; font-weight: 700; color: #16243c; line-height: 1.25; }
.bpc-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 10px; border-radius: 999px; background: #eaf4fc; color: #1a69a4; font-size: 0.8em; font-weight: 600; white-space: nowrap; }
.bpc-badge i.icon { margin: 0; font-size: 0.95em; }
.bpc-city { color: #5b6576; margin-top: 2px; }
.bpc-facts { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.bpc-fact { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border: 1px solid #e3e8ef; border-radius: 8px; font-size: 0.88em; color: #33415c; background: #fff; }
.bpc-fact i.icon { margin: 0; color: #5b6576; }
.bpc-price { flex-shrink: 0; align-self: center; text-align: right; padding-left: 20px; border-left: 1px solid #e3e8ef; min-width: 150px; }
.bpc-price-label { color: #5b6576; font-size: 0.85em; }
.bpc-price-value { color: #16243c; font-weight: 700; font-size: 1.2em; margin-top: 2px; }
.bpc-price-value.bpc-muted { color: #8a92a0; font-weight: 600; font-size: 1em; }
.bpc-pi { margin-top: 18px; background: #f3f8fd; border: 1px solid #dbe9f7; border-radius: 12px; padding: 16px 18px; }
.bpc-card.bpc-bare .bpc-pi { margin-top: 0; }
.bpc-pi-head { display: flex; align-items: flex-start; gap: 14px; }
.bpc-pi-icon { width: 40px; height: 40px; border-radius: 10px; background: #fff; border: 1px solid #dbe9f7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.bpc-pi-icon i.icon { margin: 0; color: #2185d0; font-size: 1.15em; }
.bpc-pi-text { flex: 1 1 auto; min-width: 0; }
.bpc-pi-title { margin: 0; font-size: 1.08em; font-weight: 700; color: #16243c; }
.bpc-pi-sub { color: #5b6576; font-size: 0.92em; margin-top: 2px; }
.bpc-status { display: inline-flex; align-items: center; gap: 6px; font-size: 0.9em; font-weight: 600; white-space: nowrap; flex-shrink: 0; padding-top: 2px; }
.bpc-status i.icon { margin: 0; }
.bpc-status.positive { color: #1e7a3a; }
.bpc-status.positive i.icon { color: #21ba45; }
.bpc-status.caution { color: #9a5b00; }
.bpc-status.info { color: #1a69a4; }
.bpc-status.negative { color: #b42318; }
.bpc-status.neutral { color: #5b6576; }
.bpc-message { margin: 10px 0 0 54px; color: #33415c; font-size: 0.92em; }
.bpc-pi-foot { display: flex; align-items: center; gap: 12px; margin-top: 14px; padding-left: 54px; }
.bpc-chips { flex: 1 1 auto; display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
.bpc-chip { display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: 8px; background: #fff; border: 1px solid #e3e8ef; color: #33415c; font-size: 0.84em; }
.bpc-chip i.icon { margin: 0; color: #5b6576; font-size: 0.95em; }
.bpc-action.ui.button { flex-shrink: 0; margin: 0; min-height: 40px; }
.bpc-action.ui.button i.icon { margin: 0 0 0 8px !important; opacity: 1; }
.bpc-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 16px; margin-top: 16px; }

@media (max-width: 900px) {
  .bpc-head { flex-wrap: wrap; }
  .bpc-price { flex-basis: 100%; text-align: left; border-left: 0; padding-left: 0; border-top: 1px solid #e3e8ef; padding-top: 12px; display: flex; align-items: baseline; gap: 10px; }
  .bpc-price-value { margin-top: 0; }
  .bpc-pi-foot { flex-wrap: wrap; padding-left: 0; }
  .bpc-message { margin-left: 0; }
}
@media (max-width: 600px) {
  .bpc-card { padding: 16px; }
  .bpc-head { flex-direction: column; align-items: stretch; gap: 14px; }
  .bpc-photo { width: 100%; height: 170px; }
  .bpc-price { flex-basis: auto; width: 100%; }
  .bpc-pi { padding: 14px; }
  .bpc-pi-head { flex-wrap: wrap; }
  .bpc-pi-text { flex: 1 1 0; }
  .bpc-status { flex-basis: 100%; padding: 0; }
  .bpc-action.ui.button { width: 100%; min-height: 44px; }
}
`;

const STATUS_ICON = {
  positive: "check circle",
  caution: "adjust",
  info: "circle notched",
  negative: "exclamation circle",
};

function PropertyIntelligencePanel({ panel, dateLabel, onAction }) {
  const showCoverage = panel.state !== "restricted" && panel.state !== "none";
  const statusText = panel.statusLabel ? `${panel.statusLabel}${dateLabel && (panel.state === "completed" || panel.state === "partial") ? ` ${dateLabel}` : ""}` : null;
  return (
    <section className="bpc-pi" aria-label="Property Intelligence">
      <div className="bpc-pi-head">
        <div className="bpc-pi-icon" aria-hidden="true"><Icon name="magic" /></div>
        <div className="bpc-pi-text">
          <h4 className="bpc-pi-title">Property Intelligence</h4>
          <div className="bpc-pi-sub">Property research and insights, saved to this transaction.</div>
        </div>
        {statusText && (
          <div className={`bpc-status ${panel.tone}`} role="status">
            {STATUS_ICON[panel.tone] && <Icon name={STATUS_ICON[panel.tone]} loading={panel.state === "generating"} aria-hidden="true" />}
            {statusText}
          </div>
        )}
      </div>
      {panel.message && <p className="bpc-message">{panel.message}</p>}
      {(showCoverage || panel.actionLabel) && (
        <div className="bpc-pi-foot">
          {showCoverage && (
            <ul className="bpc-chips" aria-label="Research areas covered" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {PI_COVERAGE.map((c) => (
                <li key={c.key} className="bpc-chip"><Icon name={c.icon} aria-hidden="true" />{c.label}</li>
              ))}
            </ul>
          )}
          {panel.actionLabel && onAction && (
            <Button
              primary={panel.state !== "generating"}
              basic={panel.state === "generating"}
              color={panel.state === "generating" ? "blue" : undefined}
              className="bpc-action"
              onClick={onAction}
            >
              {panel.actionLabel}
              {(panel.state === "completed" || panel.state === "partial") && <Icon name="arrow right" aria-hidden="true" />}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * One Buyer property: a compact header (photo, address, known facts, list
 * price) and its Property Intelligence panel, followed by any other visible
 * Strategy cards. `bare` drops the header and frame - used inside an
 * expanded previous-property row, which already shows the address.
 * Facts and price are only what the caller passes; nothing is guessed.
 */
export default function BuyerPropertyCard({ property, facts = [], price, isCurrent, panel, panelDateLabel, onPanelAction, otherCards = [], bare }) {
  const address = property || {};
  const cityStateZip = [address.city, [address.state, address.zipcode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <div className={`bpc-card${isCurrent ? " bpc-current" : ""}${bare ? " bpc-bare" : ""}`}>
      <style>{CARD_CSS}</style>
      {!bare && (
        <div className="bpc-head">
          <div className="bpc-photo">
            <PropertyPhoto pic={address.pic} width="100%" height="100%" borderRadius={10} />
          </div>
          <div className="bpc-info">
            <div className="bpc-title">
              <h3 className="bpc-street">{address.street}{address.unit ? ` ${address.unit}` : ""}</h3>
              {isCurrent && <span className="bpc-badge"><Icon name="home" aria-hidden="true" />Current Property</span>}
            </div>
            {cityStateZip && <div className="bpc-city">{cityStateZip}</div>}
            {facts.length > 0 && (
              <ul className="bpc-facts" aria-label="Property details" style={{ listStyle: "none", margin: "10px 0 0", padding: 0 }}>
                {facts.map((f) => <li key={f.key} className="bpc-fact"><Icon name={f.icon} aria-hidden="true" />{f.text}</li>)}
              </ul>
            )}
          </div>
          <div className="bpc-price">
            <div className="bpc-price-label">List price</div>
            <div className={`bpc-price-value${price ? "" : " bpc-muted"}`}>{price || "Price unavailable"}</div>
          </div>
        </div>
      )}
      {panel && <PropertyIntelligencePanel panel={panel} dateLabel={panelDateLabel} onAction={onPanelAction} />}
      {otherCards.length > 0 && (
        <div className="bpc-cards">
          {otherCards.map((card) => <StrategyCard key={card.key} {...card} />)}
        </div>
      )}
    </div>
  );
}
